import { useEffect, useMemo, useState } from 'react'
import Dialog from './Dialog'
import ReportRenderer from './ReportRenderer'
import type { ReportProps } from './ReportView'
import { buildCsv, buildPrompt, reportStem, type ExportFile } from '../lib/report'
import { canShare, canShareFiles, copyImage, copyText, saveFiles, shareFiles } from '../lib/share'
import { isNativeApp } from '../lib/native'
import { getFile } from '../lib/persist'

interface Props extends Omit<ReportProps, 'include'> {
  onClose: () => void
}

type Status = { kind: 'rendering' | 'ready' | 'busy' | 'done' | 'error'; text?: string }

const ALL = { summary: true, charts: true, map: true }

/**
 * Hand the comparison to an AI assistant the user already has (ChatGPT, Claude, …): the
 * report image, optionally the CSV, and a ready-made prompt. On the phone this goes through
 * the share sheet, where the user picks the app; Mala Racing itself sends nothing anywhere.
 */
export default function AskAiDialog({ onClose, ...report }: Props) {
  const [csv, setCsv] = useState(true)
  const [withSessions, setWithSessions] = useState(true)
  // The original session files, read from the cache so they are ready when the share sheet opens.
  const [sessionFiles, setSessionFiles] = useState<ExportFile[]>([])
  useEffect(() => {
    let live = true
    void Promise.all(report.ctx.sessions.map((s) => getFile(s.id))).then((stored) => {
      if (!live) return
      setSessionFiles(
        stored.flatMap((f) =>
          f
            ? [
                {
                  name: f.name.replace(/^sample /, ''),
                  blob: new Blob([f.bytes], { type: 'application/octet-stream' }),
                },
              ]
            : [],
        ),
      )
    })
    return () => {
      live = false
    }
  }, [report.ctx.sessions])
  const sessionsOn = withSessions && sessionFiles.length > 0
  const defaultPrompt = useMemo(
    () => buildPrompt(report.ctx, { image: true, csv, sessions: sessionsOn, accelMode: report.accelMode }),
    [report.ctx, report.accelMode, csv, sessionsOn],
  )
  const [prompt, setPrompt] = useState(defaultPrompt)
  const [edited, setEdited] = useState(false)
  useEffect(() => {
    if (!edited) setPrompt(defaultPrompt)
  }, [defaultPrompt, edited])

  // The image is rendered ahead of time, so sharing happens inside the tap that asked for it
  // (the browser's share sheet only opens from a user gesture).
  const [png, setPng] = useState<Blob | null>(null)
  const [status, setStatus] = useState<Status>({ kind: 'rendering', text: 'Preparing the image…' })

  const stem = reportStem(report.ctx)
  const files = (withPrompt: boolean): ExportFile[] => {
    const out: ExportFile[] = []
    if (png) out.push({ name: `${stem}.png`, blob: png })
    if (csv) out.push({ name: `${stem}.csv`, blob: new Blob([buildCsv(report.ctx)], { type: 'text/csv' }) })
    if (sessionsOn) out.push(...sessionFiles)
    if (withPrompt) out.push({ name: `${stem}-prompt.txt`, blob: new Blob([prompt], { type: 'text/plain' }) })
    return out
  }
  /** A chat link was clicked: the prompt travels in the URL, the image goes on the clipboard, the rest downloads. */
  async function onChatLink() {
    const notes: string[] = ['The chat opened with the prompt filled in.']
    if (png && (await copyImage(png)))
      notes.push('The image is on your clipboard: paste it into the message (⌘V / Ctrl+V).')
    else if (png) {
      await saveFiles([{ name: `${stem}.png`, blob: png }])
      notes.push('The image was downloaded: drag it into the message.')
    }
    const rest = files(false).filter((f) => !f.name.endsWith('.png'))
    if (rest.length) {
      await saveFiles(rest)
      notes.push(`${rest.map((f) => f.name).join(', ')} downloaded: drag them in too.`)
    }
    setStatus({ kind: 'done', text: notes.join(' ') })
  }
  const ready = status.kind === 'ready' || status.kind === 'done'
  const shareable = canShare()
  const shareTakesFiles = canShareFiles()

  async function onShare() {
    setStatus({ kind: 'busy', text: 'Opening the share sheet…' })
    try {
      const list = shareTakesFiles ? files(false) : []
      await shareFiles('Mala Racing lap comparison', prompt, list)
      setStatus({
        kind: 'done',
        text: shareTakesFiles ? 'Shared.' : 'Prompt shared; use Download for the image and CSV.',
      })
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      setStatus(/abort|cancel/i.test(msg) ? { kind: 'ready' } : { kind: 'error', text: `Share failed: ${msg}` })
    }
  }
  async function onDownload() {
    setStatus({ kind: 'busy', text: 'Saving…' })
    try {
      const list = files(true)
      await saveFiles(list)
      setStatus({
        kind: 'done',
        text: isNativeApp ? `Saved ${list.length} files to Files › Mala Racing.` : `Downloaded ${list.length} files.`,
      })
    } catch (e) {
      setStatus({ kind: 'error', text: `Save failed: ${e instanceof Error ? e.message : String(e)}` })
    }
  }
  async function onCopy() {
    setStatus(
      (await copyText(prompt))
        ? { kind: 'done', text: 'Prompt copied.' }
        : { kind: 'error', text: 'Could not copy; select the text and copy it.' },
    )
  }
  const q = encodeURIComponent(prompt)

  return (
    <Dialog title="Ask AI" onClose={onClose}>
      <p className="muted small">
        {isNativeApp
          ? 'Sends the report image, the data and the prompt below to an AI app on this phone (ChatGPT, Claude, …): pick it in the share sheet. Mala Racing sends nothing itself.'
          : 'Hands the report image, the data and the prompt below to an AI assistant you already use. Mala Racing sends nothing itself.'}
      </p>
      <div className="export-options">
        <label>
          <input type="checkbox" checked={csv} onChange={() => setCsv((v) => !v)} />
          Data as CSV (every 5 m)
        </label>
        <label>
          <input
            type="checkbox"
            checked={sessionsOn}
            disabled={sessionFiles.length === 0}
            onChange={() => setWithSessions((v) => !v)}
          />
          Session file{report.ctx.sessions.length > 1 ? 's' : ''} (
          {report.ctx.sessions.map((s) => s.name.replace(/^sample /, '')).join(', ')})
        </label>
      </div>
      <label className="export-prompt-label">
        Prompt{' '}
        {edited && (
          <button
            className="link small"
            onClick={() => {
              setEdited(false)
              setPrompt(defaultPrompt)
            }}
          >
            reset
          </button>
        )}
      </label>
      <textarea
        className="export-prompt"
        value={prompt}
        rows={isNativeApp ? 6 : 8}
        onChange={(e) => {
          setPrompt(e.target.value)
          setEdited(true)
        }}
      />
      <div className="export-actions">
        {shareable && (
          <button className="button" disabled={!ready} onClick={() => void onShare()}>
            {isNativeApp ? 'Choose an AI app…' : 'Share…'}
          </button>
        )}
        {!isNativeApp && (
          <button className="button secondary" disabled={!ready} onClick={() => void onDownload()}>
            Download files
          </button>
        )}
        <button className="button secondary" onClick={() => void onCopy()}>
          Copy prompt
        </button>
      </div>
      {!isNativeApp && (
        <p className="muted small export-links">
          Or open a chat with the prompt filled in — the image is copied for you to paste, the other files download:{' '}
          <a href={`https://chatgpt.com/?q=${q}`} target="_blank" rel="noreferrer" onClick={() => void onChatLink()}>
            ChatGPT
          </a>{' '}
          ·{' '}
          <a href={`https://claude.ai/new?q=${q}`} target="_blank" rel="noreferrer" onClick={() => void onChatLink()}>
            Claude
          </a>
        </p>
      )}
      <p className={`small export-status ${status.kind === 'error' ? 'error-text' : 'muted'}`}>{status.text ?? ' '}</p>
      {!png && status.kind === 'rendering' && (
        <ReportRenderer
          {...report}
          include={ALL}
          onResult={(r) => {
            if ('png' in r) {
              setPng(r.png)
              setStatus({ kind: 'ready' })
            } else setStatus({ kind: 'error', text: `Could not draw the report: ${r.error}` })
          }}
        />
      )}
    </Dialog>
  )
}
